import type { DatabaseFilterOperator } from './query-types.js';

export type FilterValueInputKind =
  | 'none'
  | 'text'
  | 'number'
  | 'checkbox'
  | 'date'
  | 'select';

export type FilterOperatorOption = {
  operator: DatabaseFilterOperator;
  label: string;
  valueInput: FilterValueInputKind;
};

const OP = (
  operator: DatabaseFilterOperator,
  label: string,
  valueInput: FilterValueInputKind,
): FilterOperatorOption => ({ operator, label, valueInput });

const TEXT_OPS: FilterOperatorOption[] = [
  OP('contains', 'contains', 'text'),
  OP('eq', 'is', 'text'),
  OP('neq', 'is not', 'text'),
  OP('is_empty', 'is empty', 'none'),
  OP('is_not_empty', 'is not empty', 'none'),
];

const NUMBER_OPS: FilterOperatorOption[] = [
  OP('eq', '=', 'number'),
  OP('neq', '≠', 'number'),
  OP('gt', '>', 'number'),
  OP('gte', '≥', 'number'),
  OP('lt', '<', 'number'),
  OP('lte', '≤', 'number'),
  OP('is_empty', 'is empty', 'none'),
  OP('is_not_empty', 'is not empty', 'none'),
];

const CHECKBOX_OPS: FilterOperatorOption[] = [OP('eq', 'is', 'checkbox')];

const DATE_OPS: FilterOperatorOption[] = [
  OP('eq', 'is', 'date'),
  OP('gt', 'after', 'date'),
  OP('gte', 'on or after', 'date'),
  OP('lt', 'before', 'date'),
  OP('lte', 'on or before', 'date'),
  OP('is_empty', 'is empty', 'none'),
  OP('is_not_empty', 'is not empty', 'none'),
];

const SELECT_LIKE_OPS: FilterOperatorOption[] = [
  OP('eq', 'is', 'select'),
  OP('neq', 'is not', 'select'),
  OP('is_empty', 'is empty', 'none'),
  OP('is_not_empty', 'is not empty', 'none'),
];

/** Field id for row title filters. */
export const TITLE_FILTER_FIELD = 'title' as const;

export function filterOperatorsForColumnType(columnType: string): FilterOperatorOption[] {
  switch (columnType) {
    case TITLE_FILTER_FIELD:
    case 'text':
    case 'url':
    case 'email':
    case 'phone':
      return TEXT_OPS;
    case 'number':
      return NUMBER_OPS;
    case 'checkbox':
      return CHECKBOX_OPS;
    case 'date':
      return DATE_OPS;
    case 'select':
    case 'multi_select':
    case 'relation':
      return SELECT_LIKE_OPS;
    default:
      return TEXT_OPS;
  }
}

export function operatorNeedsValue(operator: DatabaseFilterOperator): boolean {
  return operator !== 'is_empty' && operator !== 'is_not_empty';
}

export function coerceFilterValue(
  columnType: string,
  operator: DatabaseFilterOperator,
  raw: string | boolean,
): unknown {
  if (!operatorNeedsValue(operator)) {
    return undefined;
  }
  if (columnType === 'checkbox') {
    return raw === true || raw === 'true';
  }
  if (columnType === 'number') {
    const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
    return Number.isFinite(n) ? n : raw;
  }
  return typeof raw === 'string' ? raw : String(raw);
}

export function defaultOperatorForType(columnType: string): DatabaseFilterOperator {
  const ops = filterOperatorsForColumnType(columnType);
  return ops[0]?.operator ?? 'contains';
}
